import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Textarea } from '@/components/ui/textarea.js';
import { Brain, Plus, Search, Pin, Tag, Trash2 } from 'lucide-react';
import { cn, formatDate } from '@/lib/utils.js';
import type { Memory } from '@/types/api.js';
import { LoadingState } from '@/components/common/LoadingState.js';

interface MemoriesPanelProps {
  hideHeader?: boolean;
}

export function MemoriesPanel({ hideHeader = false }: MemoriesPanelProps) {
  const { api } = useAuth();
  const [memories, setMemories] = useState<Memory[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [newMemoryContent, setNewMemoryContent] = useState('');
  const [newMemoryTags, setNewMemoryTags] = useState('');

  useEffect(() => {
    loadMemories();
  }, []);

  const loadMemories = async () => {
    try {
      setLoading(true);
      const data = await api.getMemories({ limit: 100 });
      setMemories(data.sort((a, b) => b.updatedAt - a.updatedAt));
    } catch (error) {
      console.error('Failed to load memories:', error);
      // Don't show error to user - just show empty state
      setMemories([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newMemoryContent.trim()) return;

    try {
      const tags = newMemoryTags
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);

      const memory = await api.createMemory({
        content: newMemoryContent.trim(),
        tags: tags.length > 0 ? tags : undefined,
      });

      setMemories((prev) => [memory, ...prev]);
      setNewMemoryContent('');
      setNewMemoryTags('');
      setIsCreating(false);
    } catch (error) {
      console.error('Failed to create memory:', error);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await api.deleteMemory(id);
      setMemories((prev) => prev.filter((m) => m.id !== id));
    } catch (error) {
      console.error('Failed to delete memory:', error);
    }
  };

  const handleTogglePin = async (memory: Memory) => {
    try {
      const updated = await api.updateMemory(memory.id, {
        pinned: !memory.metadata?.pinned,
      });
      setMemories((prev) =>
        prev.map((m) => (m.id === memory.id ? updated : m))
      );
    } catch (error) {
      console.error('Failed to toggle pin:', error);
    }
  };

  const filteredMemories = memories.filter((memory) =>
    memory.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const pinnedMemories = filteredMemories.filter((m) => m.metadata?.pinned);
  const unpinnedMemories = filteredMemories.filter((m) => !m.metadata?.pinned);

  if (loading) {
    return <LoadingState message="Loading memories..." />;
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      {!hideHeader && (
        <div className="p-4 border-b border-border/50">
          <h2 className="text-lg font-semibold mb-2">What I Remember</h2>
          <p className="text-xs text-muted-foreground mb-3">
            Quick facts and preferences I'll use to personalize our conversations
          </p>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search what I know..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-9"
            />
          </div>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto scrollbar-thin">
        {isCreating && (
          <div className="p-4 border-b border-border/50 space-y-3 bg-muted/20">
            <Textarea
              placeholder="What should I remember about you?"
              value={newMemoryContent}
              onChange={(e) => setNewMemoryContent(e.target.value)}
              className="min-h-[80px] resize-none"
            />
            <Input
              placeholder="Tags (optional, comma-separated)"
              value={newMemoryTags}
              onChange={(e) => setNewMemoryTags(e.target.value)}
              className="h-9"
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={handleCreate} className="flex-1">
                Save
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setIsCreating(false);
                  setNewMemoryContent('');
                  setNewMemoryTags('');
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {memories.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full p-8 text-center">
            <Brain className="h-12 w-12 text-muted-foreground mb-4 opacity-50" />
            <p className="text-sm text-muted-foreground mb-2">I don't remember anything yet</p>
            <p className="text-xs text-muted-foreground/70">
              Add memories so I can assist you better
            </p>
          </div>
        ) : (
          <div className="p-2 space-y-4">
            {/* Pinned Section */}
            {pinnedMemories.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-muted-foreground uppercase mb-2 px-2">
                  Pinned
                </p>
                <div className="space-y-1">
                  {pinnedMemories.map((memory) => (
                    <MemoryCard
                      key={memory.id}
                      memory={memory}
                      onDelete={handleDelete}
                      onTogglePin={handleTogglePin}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* All Memories */}
            {unpinnedMemories.length > 0 && (
              <div>
                {pinnedMemories.length > 0 && (
                  <p className="text-xs font-semibold text-muted-foreground uppercase mb-2 px-2">
                    All Memories
                  </p>
                )}
                <div className="space-y-1">
                  {unpinnedMemories.map((memory) => (
                    <MemoryCard
                      key={memory.id}
                      memory={memory}
                      onDelete={handleDelete}
                      onTogglePin={handleTogglePin}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* New Button */}
      <div className="p-4 border-t border-border/50">
        <Button
          className="w-full gap-2"
          variant="default"
          onClick={() => setIsCreating(true)}
          disabled={isCreating}
        >
          <Plus className="h-4 w-4" />
          Add Memory
        </Button>
      </div>
    </div>
  );
}

interface MemoryCardProps {
  memory: Memory;
  onDelete: (id: string) => void;
  onTogglePin: (memory: Memory) => void;
}

function MemoryCard({ memory, onDelete, onTogglePin }: MemoryCardProps) {
  return (
    <div className="group relative p-3 rounded-lg hover:bg-muted transition-colors">
      <div className="flex items-start justify-between gap-2 mb-2">
        <p className="text-sm text-foreground flex-1">{memory.content}</p>
        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => onTogglePin(memory)}
          >
            <Pin
              className={cn(
                'h-4 w-4',
                memory.metadata?.pinned ? 'fill-primary text-primary' : ''
              )}
            />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => onDelete(memory.id)}
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </div>

      {memory.tags && memory.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {memory.tags.map((tag, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-xs text-primary"
            >
              <Tag className="h-3 w-3" />
              {tag}
            </span>
          ))}
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        {formatDate(memory.updatedAt)}
        {memory.metadata?.source === 'system' && ' • System'}
      </p>
    </div>
  );
}
